import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, PageSkeleton } from "@/components/ui";
import { ReportsIcon } from "@/components/icons";
import { BuilderShell } from "@/components/reporting/builder/builder-shell";
import { useReport } from "@/hooks/use-reports";
import { useAppStore } from "@/store";

export const Route = createFileRoute("/_authenticated/reports/$id/edit")({
  component: EditReportRoute,
});

/**
 * One saved report, open in the builder. This is the only reporting route that
 * reads before it renders: the shell hydrates from its `seed` at construction
 * under `key={id}`, so it may not mount until the stored document is in hand.
 *
 * A foreign, missing or archived id is a 404, and mounting the builder over no
 * document would seed an EMPTY one that the next Save would write back over the
 * report — so the failure short-circuits to the same not-found rendering the
 * detail surface takes, with the same way back, and never reaches the shell.
 *
 * The full-bleed frame is the builder's, and it is the same reserve arithmetic
 * `/reports/new` explains.
 */
function EditReportRoute() {
  const { id } = Route.useParams();
  const { t } = useTranslation(["reporting", "org"]);
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);

  const reportQuery = useReport(orgId, id);
  const report = reportQuery.data;

  const backToList = () => {
    void navigate({ to: "/reports" });
  };

  const backToReport = () => {
    void navigate({ to: "/reports/$id", params: { id } });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  if (report === undefined) {
    return (
      <div className="p-8">
        {reportQuery.isError ? (
          <EmptyState
            icon={<ReportsIcon />}
            title={t("detail.notFound.title")}
            description={t("detail.notFound.body")}
            actionLabel={t("detail.backToReports")}
            onAction={backToList}
          />
        ) : (
          <PageSkeleton />
        )}
      </div>
    );
  }

  return (
    <div className="-mb-24 h-[calc(100%+6rem)] print:mb-0 print:h-full">
      <BuilderShell
        key={id}
        orgId={orgId}
        reportId={id}
        seed={report}
        onSaved={(reportId) => {
          void navigate({ to: "/reports/$id", params: { id: reportId } });
        }}
        onDiscard={backToReport}
        onBack={backToReport}
        onOpenReports={backToList}
      />
    </div>
  );
}
